import { DateTime24 } from "./DateTime24";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import AddressAutocomplete from "./AddressAutocomplete";
import { updateImpound, fetchImpoundConfig } from "../lib/api";
import { toTenantWall, wallToDate } from "../lib/tenantTime";

/**
 * Dialog for editing an in-progress impound.
 *
 * Editable fields the dispatcher actually needs day-to-day:
 *  - Owner contact (name, phone)
 *  - Vehicle (make/model, plate, GVM)
 *  - Term + impounded-at (recomputes release_due_at server-side)
 *  - Notice # / Police reference
 *  - Public-holiday flag (affects pricing)
 *  - Notes
 *
 * Locked fields (require admin / undo flow): addresses, geolocation, status.
 * Backend rejects edits to released or NTS-signed-over impounds.
 *
 * Converts the impounded_at ISO to/from a `<input type="datetime-local">`
 * which is the native picker on phones AND desktops — no third-party deps.
 */
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return toTenantWall(d);
}

function localInputToIso(s) {
  if (!s) return null;
  // Picker value is tenant-local wall time.
  const d = wallToDate(s);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function EditImpoundDialog({ open, onOpenChange, impound, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [yards, setYards] = useState([]);

  // Load yards from police-impound config so the dropdown shows the same
  // friendly names ("Parkers Road", "Blackbyre") that NewImpoundDialog uses.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchImpoundConfig()
      .then((cfg) => {
        if (cancelled) return;
        const ys = (cfg?.yards || []).filter((y) => y && (y.address || "").trim());
        setYards(ys);
      })
      .catch(() => setYards([]));
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (open && impound) {
      setForm({
        customer_name: impound.customer_name || "",
        customer_phone: impound.customer_phone || "",
        impound_owner_address: impound.impound_owner_address || "",
        vehicle: impound.vehicle || "",
        rego: impound.rego || "",
        gvm_kg: impound.gvm_kg ?? "",
        impound_term: impound.impound_term || "28d",
        impounded_at_local: isoToLocalInput(impound.impounded_at),
        impound_notice_number: impound.impound_notice_number || "",
        police_reference: impound.police_reference || "",
        tow_authority_number: impound.tow_authority_number || "",
        claim_number: impound.claim_number || "",
        // Pickup (where police got the vehicle) — geocoded coords keep
        // round-trip distance / quote accurate when address is corrected.
        address: impound.address || "",
        lat: impound.lat ?? null,
        lng: impound.lng ?? null,
        // Yard (where it's stored). Stored as `drop_off_address`. When the
        // dispatcher picks from the dropdown we also stamp the matching
        // lat/lng so the round-trip recomputes server-side.
        drop_off_address: impound.drop_off_address || "",
        drop_off_lat: impound.drop_off_lat ?? null,
        drop_off_lng: impound.drop_off_lng ?? null,
        notes: impound.notes || "",
        is_public_holiday: !!impound.is_public_holiday,
      });
    }
  }, [open, impound]);

  if (!impound) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        impound_owner_address: form.impound_owner_address,
        vehicle: form.vehicle.trim(),
        rego: form.rego.trim() || null,
        gvm_kg: form.gvm_kg === "" ? null : parseFloat(form.gvm_kg),
        impound_term: form.impound_term,
        impounded_at: localInputToIso(form.impounded_at_local),
        impound_notice_number: form.impound_notice_number.trim() || null,
        police_reference: form.police_reference.trim() || null,
        tow_authority_number: form.tow_authority_number.trim() || null,
        claim_number: form.claim_number.trim() || null,
        address: form.address ? form.address.trim() : null,
        lat: form.lat,
        lng: form.lng,
        drop_off_address: form.drop_off_address || null,
        drop_off_lat: form.drop_off_lat,
        drop_off_lng: form.drop_off_lng,
        notes: form.notes,
        is_public_holiday: form.is_public_holiday,
      };
      const updated = await updateImpound(impound.id, payload);
      toast.success("Impound updated — release-due and quote refreshed");
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // When dispatcher picks a yard from the dropdown, also stamp the matching
  // lat/lng from config so the server can recompute round-trip distance.
  const onYardPick = (addr) => {
    const y = yards.find((x) => x.address === addr);
    setForm((f) => ({
      ...f,
      drop_off_address: addr,
      drop_off_lat: y?.lat ?? null,
      drop_off_lng: y?.lng ?? null,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-impound-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black tracking-tight">Edit Impound</DialogTitle>
          <DialogDescription>
            <strong className="text-slate-800 dark:text-slate-100">{impound.vehicle}</strong>
            {impound.rego && <> · <span className="font-mono">{impound.rego}</span></>}
            <br />
            <span className="text-xs text-slate-500">
              Changing the impounded-at date or term recomputes the release-due timer + live quote.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="uppercase-label">Owner Name</Label>
              <Input value={form.customer_name || ""} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} data-testid="edit-impound-name" />
            </div>
            <div>
              <Label className="uppercase-label">Owner Phone</Label>
              <Input value={form.customer_phone || ""} onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))} data-testid="edit-impound-phone" />
            </div>
          </div>
          <div>
            <Label className="uppercase-label">Owner Residential Address</Label>
            <AddressAutocomplete
              value={form.impound_owner_address || ""}
              onChange={(v) => setForm((f) => ({ ...f, impound_owner_address: v }))}
              onPick={({ label }) => setForm((f) => ({ ...f, impound_owner_address: label }))}
              placeholder="Start typing the owner's home address…"
              testIdPrefix="edit-impound-owner-address"
            />
            <div className="text-[10px] text-slate-500 mt-1">Used on the owner-notice letter. Suggests as you type.</div>
          </div>

          {/* Pickup location — where police impounded the vehicle from.
              Changing this re-routes round-trip + reprices the quote. */}
          <div>
            <Label className="uppercase-label">Pickup Location (where impounded from)</Label>
            <AddressAutocomplete
              value={form.address || ""}
              onChange={(v) => setForm((f) => ({ ...f, address: v, lat: null, lng: null }))}
              onPick={({ label, lat, lng }) => setForm((f) => ({ ...f, address: label, lat, lng }))}
              placeholder="Start typing the pickup address…"
              testIdPrefix="edit-impound-pickup"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              Re-routes round-trip distance + repricing.
              {form.lat != null && form.lng != null
                ? ` ✓ Geocoded: ${Number(form.lat).toFixed(4)}, ${Number(form.lng).toFixed(4)}`
                : " ○ Pick a suggestion to geocode."}
            </div>
          </div>

          {/* Drop-off yard — pick from configured yards. Stores the friendly
              name's matching address + lat/lng. */}
          <div>
            <Label className="uppercase-label">Storage Yard</Label>
            <Select value={form.drop_off_address || ""} onValueChange={onYardPick}>
              <SelectTrigger data-testid="edit-impound-yard">
                <SelectValue placeholder={yards.length ? "Select yard…" : "No yards configured — admin must set under Integrations"} />
              </SelectTrigger>
              <SelectContent>
                {yards.map((y, idx) => (
                  <SelectItem key={`yard-${idx}`} value={y.address}>
                    {y.name || y.address}
                    {y.name && y.name !== y.address ? ` · ${y.address}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-slate-500 mt-1">
              {form.drop_off_address
                ? `Currently: ${form.drop_off_address}`
                : "Pick the yard where the vehicle is stored."}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="uppercase-label">Vehicle</Label>
              <Input value={form.vehicle || ""} onChange={(e) => setForm((f) => ({ ...f, vehicle: e.target.value }))} data-testid="edit-impound-vehicle" />
            </div>
            <div>
              <Label className="uppercase-label">Rego</Label>
              <Input value={form.rego || ""} onChange={(e) => setForm((f) => ({ ...f, rego: e.target.value.toUpperCase() }))} className="font-mono uppercase" data-testid="edit-impound-rego" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="uppercase-label">GVM (kg)</Label>
              <Input type="number" inputMode="numeric" min="0" value={form.gvm_kg ?? ""} onChange={(e) => setForm((f) => ({ ...f, gvm_kg: e.target.value }))} data-testid="edit-impound-gvm" />
            </div>
            <div>
              <Label className="uppercase-label">Term</Label>
              <Select value={form.impound_term} onValueChange={(v) => setForm((f) => ({ ...f, impound_term: v }))}>
                <SelectTrigger data-testid="edit-impound-term"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="28d">28 Days</SelectItem>
                  <SelectItem value="6mo">6 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="uppercase-label">Impounded At (date + time, 24h)</Label>
            <DateTime24
              value={form.impounded_at_local || ""}
              onChange={(v) => setForm((f) => ({ ...f, impounded_at_local: v }))}
              dataTestId="edit-impound-impounded-at"
            />
            <div className="text-[10px] text-slate-500 mt-1">Recomputes release-due to {form.impound_term === "6mo" ? "6 months" : "28 days"} after this.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="uppercase-label">Notice #</Label>
              <Input value={form.impound_notice_number || ""} onChange={(e) => setForm((f) => ({ ...f, impound_notice_number: e.target.value }))} data-testid="edit-impound-notice" />
            </div>
            <div>
              <Label className="uppercase-label">Police Ref</Label>
              <Input value={form.police_reference || ""} onChange={(e) => setForm((f) => ({ ...f, police_reference: e.target.value }))} data-testid="edit-impound-pref" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="uppercase-label">Tow Authority #</Label>
              <Input
                value={form.tow_authority_number || ""}
                onChange={(e) => setForm((f) => ({ ...f, tow_authority_number: e.target.value.toUpperCase() }))}
                placeholder="e.g. TA0042"
                className="font-mono uppercase"
                data-testid="edit-impound-tow-auth"
              />
            </div>
            <div>
              <Label className="uppercase-label">Claim Number</Label>
              <Input
                value={form.claim_number || ""}
                onChange={(e) => setForm((f) => ({ ...f, claim_number: e.target.value }))}
                placeholder="Insurer claim ref (optional)"
                data-testid="edit-impound-claim"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm py-1 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.is_public_holiday}
              onChange={(e) => setForm((f) => ({ ...f, is_public_holiday: e.target.checked }))}
              className="rounded"
              data-testid="edit-impound-holiday"
            />
            <span className="text-slate-700 dark:text-slate-200">Impounded on a public holiday (affects pricing)</span>
          </label>
          <div>
            <Label className="uppercase-label">Notes</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="edit-impound-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="edit-impound-submit">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
